import { AppNS } from 'app';
import { apiPublishCompany } from 'app/api_v2/companies';
import { updateCacheItemProp } from 'app/core/cache/helpers';
import { assetUrl } from 'app/helpers/assetUrl';
import ButtonLoader from 'app/ui_v2/components/button-loader/ButtonLoader';
import { ConfirmationDialog } from 'app/ui_v2/components/confirmation-dialog/ConfirmationDialog';
import ErrorsList from 'app/ui_v2/components/errors-list/ErrorsList';
import AddIcon from 'app/ui_v2/icons/AddIcon';
import { useState } from 'react';

import { Box, Button, Typography, useTheme } from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { updateCompanyInEmploymentCache } from '../helpers';
import { isProfileCanBePublished } from './helpers';

export interface PublishProfileProps {
  data: AppNS.Company;
}

export default function PublishProfile(props: PublishProfileProps) {
  const { data } = props;

  const theme = useTheme();
  const [open, setOpen] = useState<boolean>(false);

  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: (id: string) => apiPublishCompany(id),
    onSuccess: (data: AppNS.Company) => {
      const updatedItem = updateCacheItemProp<AppNS.Company>(queryClient, ['companies', data.id], {
        state: data.state,
      });
      if (updatedItem) {
        updateCompanyInEmploymentCache(updatedItem, queryClient);
      }
    },
  });

  const handleAction = () => {
    mutation.mutate(data.id);
  };

  return (
    <>
      <Button
        variant="contained"
        color="primary"
        disableElevation
        onClick={() => {
          setOpen(true);
        }}
        disabled={!isProfileCanBePublished(data)}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            width: '24px',
            height: '24px',
          }}
        >
          {!mutation.isLoading && <AddIcon />}
          {mutation.isLoading && <ButtonLoader isActive={mutation.isLoading} />}
        </Box>
        <Box ml={1}>Publish Profile</Box>
      </Button>
      {mutation.isError && mutation.error && (
        <Box mt={3}>
          <ErrorsList title="Unexpected error" errors={mutation.error as string[]} />
        </Box>
      )}

      <ConfirmationDialog
        title=""
        description={
          <Box mt={2} textAlign="center">
            <Typography component="h2" variant="h2" sx={{ color: theme.palette.primary.main }}>
              Ready To Publish?
            </Typography>
            <Typography component="h3" variant="h4">
              Publishing your profile will make it available for clients and DOB. You can update your profile anytime.
              <br />
              After review by DOB the profile will get the verified badge next to the agency name.
              <Box
                component="img"
                sx={{ display: 'inline-block', mb: -2 }}
                src={assetUrl('/images/badges/valid.svg')}
                alt="Certified"
                width="38"
              />
            </Typography>
          </Box>
        }
        opened={open}
        confirmButtonVariant="contained"
        confirmButtonClass="secondary"
        onConfirm={() => {
          setOpen(false);
          handleAction();
        }}
        onCancel={() => {
          setOpen(false);
        }}
        confirmButtonText="Publish"
      />
    </>
  );
}
